import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import './BalanceSheetWidget.css';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import {
  formatNumber,
  formatKMBT,
  getCssVariable,
  getDecimalPlaces,
} from '../../utils/utils';
import { ThemeType, WidgetNameSource } from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import moment from 'moment';
import { getBalanceSheet } from '../../api/company.api';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import ReactECharts from 'echarts-for-react';
import { FiscalYear, WidgetMessageType } from '../../utils/type';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
export interface BalanceSheetWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  select?: FiscalYear;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

interface BalanceSheetData {
  data_index: number;
  ric: string;
  period_end_date: string;
  fiscal_year: number;
  totalCurrentAssets: number;
  totalNonCurrentAssets: number;
  totalAssets: number;
  totalShortTermDebt: number;
  totalCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  totalLongTermDebt: number;
  totalDebt: number;
  minorityInterest: number | null;
  totalLiabilities: number;
  totalEquity: number;
  totalLiabilitiesShareholdersEquity: number;
  totalCommonSharesOutstanding: number;
  BVPS: number;
  PBR: number;
  curr_fin: string | null;
  // pre-computed growth fields (percentage, e.g. 13.5 means +13.5%)
  totalAssetsGrowth?: number | null;
  totalLiabilitiesGrowth?: number | null;
  totalEquityGrowth?: number | null;
  totalDebtGrowth?: number | null;
}

interface ExpandedRows {
  [key: string]: boolean;
}

const BalanceSheetWidget = (props: BalanceSheetWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.BalanceSheet,
    onHeightChange,
  );

  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'balanceSheet');
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { isSmallScreen } = useContainerWidth(containerRef);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [currFin, setCurrFin] = useState<string | null>(null);
  const [data, setData] = useState<BalanceSheetData[]>([]);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Filter data for small screen - show only selected fiscal year
  const displayData = useMemo(() => {
    if (!isSmallScreen || data.length === 0) {
      // Desktop: show last 5 years only
      return data.length > 5 ? data.slice(-5) : data;
    }
    // data_index from BE: 1 = latest, 2 = prior 1 year, ..., 6 = prior 5 years
    const dataIndexMap: { [key: string]: number } = {
      [FiscalYear.Latest]: 1,
      [FiscalYear.Prior1Year]: 2,
      [FiscalYear.Prior2Years]: 3,
      [FiscalYear.Prior3Years]: 4,
      [FiscalYear.Prior4Years]: 5,
      [FiscalYear.Prior5Years]: 6,
    };
    const targetIndex = dataIndexMap[props.select ?? FiscalYear.Latest];
    const found = data.find((item) => item.data_index === targetIndex);
    // Fallback to earliest available if requested year was sliced out
    return [found ?? data[0]];
  }, [data, isSmallScreen, props.select]);

  // Hide tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev) => ({ ...prev, show: false }));
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Call API when props changes

  // Format value to raw value
  const formatMillion = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    return value * 1000000;
  };
  // Calculate growth percentage
  const calculateGrowth = (
    current: number,
    previous: number | null,
  ): number | null => {
    if (previous === null || previous === 0) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Format growth percentage
  const formatGrowth = (
    growth: number | null,
  ): { value: string; isPositive: boolean } => {
    if (growth === null) return { value: '-', isPositive: true };
    const formatted = formatNumber(
      growth.toString(),
      2,
      2,
      false,
      true,
      false,
      '%',
      '-',
    );
    return { value: formatted, isPositive: growth >= 0 };
  };

  // Bar chart config for Total assets, Total liabilities, Total equity
  const barChartOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // x-axis labels (fiscal years)
    const years = data.map((item) => item.fiscal_year.toString());
    const totalAssetsSeries = data.map((item) =>
      formatMillion(item.totalAssets as number | null),
    );
    const totalLiabilitiesSeries = data.map((item) =>
      formatMillion(item.totalLiabilities as number | null),
    );

    return {
      tooltip: {
        appendTo: 'body',
        confine: true,
        margin: 20,
        outside: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          if (!params || !params.length) return '';
          const yearLabel = params[0].axisValue;
          const lines = params
            .filter((p) => p.value !== null && p.value !== undefined)
            .map((p) => {
              const valStr = `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`;
              return `${p.marker} ${p.seriesName}: ${valStr}`;
            });
          return [yearLabel, ...lines].join('<br/>');
        },
      },
      legend: {
        bottom: 0,
        left: 'center',
        data: ['Total assets', 'Total liabilities'],
        textStyle: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
        itemWidth: 12,
        itemHeight: 8,
      },
      grid: {
        left: 8,
        right: 36,
        top: 20,
        bottom: 50,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        splitLine: {
          lineStyle: { color: '#F0F0F0' },
        },
        axisLabel: {
          interval: 0,
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
          formatter: (val: number) => {
            if (val === 0) return '0';
            return `${formatKMBT(val.toString(), 1, 1, false).value}${formatKMBT(val.toString(), 1, 1, false).suffix}`;
          },
        },
      },
      series: [
        {
          name: 'Total assets',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-primary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: totalAssetsSeries,
        },
        {
          name: 'Total liabilities',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-secondary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: totalLiabilitiesSeries,
        },
      ],
    };
  }, [data, isSmallScreen, theme]);

  const fetchBalanceSheetData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: any = await getBalanceSheet(
        props.symbol || '',
        props.token || '',
      );
      if (res) {
        //find curr_fin from res.data.
        const curr_fin = res.data.find(
          (item: any) => item.curr_fin !== null,
        )?.curr_fin;
        setCurrFin(curr_fin);
        // 1) Sort all years by fiscal year (ascending)
        const sortedData: BalanceSheetData[] = [...res.data].sort(
          (a: BalanceSheetData, b: BalanceSheetData) =>
            a.fiscal_year - b.fiscal_year,
        );

        // 2) Pre-compute growth using full history (before limiting to 5 years)
        const enrichedData: BalanceSheetData[] = sortedData.map(
          (item, index, arr) => {
            const prev = index > 0 ? arr[index - 1] : null;

            const totalAssetsGrowth = prev
              ? calculateGrowth(item.totalAssets, prev.totalAssets)
              : null;
            const totalLiabilitiesGrowth = prev
              ? calculateGrowth(item.totalLiabilities, prev.totalLiabilities)
              : null;
            const totalEquityGrowth = prev
              ? calculateGrowth(item.totalEquity, prev.totalEquity)
              : null;
            const totalDebtGrowth = prev
              ? calculateGrowth(item.totalDebt, prev.totalDebt)
              : null;

            return {
              ...item,
              totalAssetsGrowth,
              totalLiabilitiesGrowth,
              totalEquityGrowth,
              totalDebtGrowth,
            };
          },
        );

        setData(enrichedData);
        setIsEmptyData(enrichedData.length === 0);
        if (enrichedData.length === 0)
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'BalanceSheetWidget',
            data: null,
          });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch balance sheet data');
      console.error('Error fetching balance sheet data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  useEffect(() => {
    fetchBalanceSheetData();
  }, [props.token, props.symbol, fetchBalanceSheetData]);

  // Tooltip positioning
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    let x = e.pageX + 15;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    if (x < 0) {
      x = 10;
    }

    return { x, y };
  };

  // Toggle row expansion
  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  return (
    <Layout theme={theme}>
      <div ref={containerRef} className="bs-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="bs-content-wrapper">
            <div className="bs-chart-container">
              {data && data.length > 0 && (
                <ReactECharts
                  option={barChartOption}
                  style={{
                    width: '100%',
                    height: 180,
                    marginTop: 20,
                    paddingTop: 20,
                  }}
                />
              )}
            </div>
            <div ref={tableContainerRef} className="bs-table-container">
              <table className="bs-table">
                <thead>
                  <tr>
                    <th></th>
                    <th className="bs-table-header-label">
                      <div className="bs-currency-label">
                        Unit million ({currFin ?? ''})
                      </div>
                    </th>
                    {displayData.map((item) => (
                      <th
                        key={item.fiscal_year}
                        className="bs-table-header-year"
                      >
                        <div className="bs-header-content">
                          <div className="bs-header-fy">
                            FY{item.fiscal_year}
                          </div>
                          <div className="bs-header-date">
                            {moment(item.period_end_date).format('DD MMM YYYY')}
                          </div>
                        </div>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Total assets */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <div
                        className="bs-label-with-icon"
                        onClick={() => toggleRow('totalAssets')}
                      >
                        <ArrowDownIcon
                          className={`bs-expand-icon ${expandedRows['totalAssets'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="bs-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total assets',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total assets
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalAssets !== null &&
                        item.totalAssets !== undefined
                          ? formatNumber(item.totalAssets.toString(), dp, dp)
                          : '-';
                      const growthFormatted =
                        item.totalAssetsGrowth !== undefined &&
                        item.totalAssetsGrowth !== null
                          ? formatGrowth(item.totalAssetsGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`bs-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Total assets */}
                  {expandedRows['totalAssets'] && (
                    <>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total current assets',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total current assets
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalCurrentAssets !== null &&
                            item.totalCurrentAssets !== undefined
                              ? formatNumber(
                                  item.totalCurrentAssets.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total non-current assets',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total non-current assets
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalNonCurrentAssets !== null &&
                            item.totalNonCurrentAssets !== undefined
                              ? formatNumber(
                                  item.totalNonCurrentAssets.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Total liabilities */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <div
                        className="bs-label-with-icon"
                        onClick={() => toggleRow('totalLiabilities')}
                      >
                        <ArrowDownIcon
                          className={`bs-expand-icon ${expandedRows['totalLiabilities'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="bs-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total liabilities',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total liabilities
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalLiabilities !== null &&
                        item.totalLiabilities !== undefined
                          ? formatNumber(
                              item.totalLiabilities.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.totalLiabilitiesGrowth !== undefined &&
                        item.totalLiabilitiesGrowth !== null
                          ? formatGrowth(item.totalLiabilitiesGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`bs-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Total liabilities */}
                  {expandedRows['totalLiabilities'] && (
                    <>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total current liabilities',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total current liabilities
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalCurrentLiabilities !== null &&
                            item.totalCurrentLiabilities !== undefined
                              ? formatNumber(
                                  item.totalCurrentLiabilities.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total non-current liabilities',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total non-current liabilities
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalNonCurrentLiabilities !== null &&
                            item.totalNonCurrentLiabilities !== undefined
                              ? formatNumber(
                                  item.totalNonCurrentLiabilities.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Total equity */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <div
                        className="bs-label-with-icon"
                        onClick={() => toggleRow('totalEquity')}
                      >
                        <ArrowDownIcon
                          className={`bs-expand-icon ${expandedRows['totalEquity'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="bs-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total equity',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total equity
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalEquity !== null &&
                        item.totalEquity !== undefined
                          ? formatNumber(item.totalEquity.toString(), dp, dp)
                          : '-';
                      const growthFormatted =
                        item.totalEquityGrowth !== undefined &&
                        item.totalEquityGrowth !== null
                          ? formatGrowth(item.totalEquityGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`bs-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Total equity */}
                  {expandedRows['totalEquity'] && (
                    <>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: "Total shareholders' equity",
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total shareholders' equity
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          // Calculate shareholders' equity as totalEquity - minorityInterest
                          const shareholdersEquity =
                            item.totalEquity !== null &&
                            item.totalEquity !== undefined
                              ? item.totalEquity - (item.minorityInterest || 0)
                              : null;
                          const displayValue =
                            shareholdersEquity !== null
                              ? formatNumber(
                                  shareholdersEquity.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Minority interest',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Minority interest
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.minorityInterest !== null &&
                            item.minorityInterest !== undefined
                              ? formatNumber(
                                  item.minorityInterest.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Total liabilities and shareholders' equity */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <span
                        className="bs-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content:
                              "Total liabilities and shareholders' equity",
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Total liabilities and shareholders' equity
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalLiabilitiesShareholdersEquity !== null &&
                        item.totalLiabilitiesShareholdersEquity !== undefined
                          ? formatNumber(
                              item.totalLiabilitiesShareholdersEquity.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Total debt */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <div
                        className="bs-label-with-icon"
                        onClick={() => toggleRow('totalDebt')}
                      >
                        <ArrowDownIcon
                          className={`bs-expand-icon ${expandedRows['totalDebt'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="bs-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total debt',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total debt
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalDebt !== null && item.totalDebt !== undefined
                          ? formatNumber(item.totalDebt.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Total debt */}
                  {expandedRows['totalDebt'] && (
                    <>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total long-term debt',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total long-term debt
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalLongTermDebt !== null &&
                            item.totalLongTermDebt !== undefined
                              ? formatNumber(
                                  item.totalLongTermDebt.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                      <tr className="bs-table-row bs-expanded-row">
                        <td></td>
                        <td
                          className="bs-table-label-cell bs-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total short-term debt',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total short-term debt
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalShortTermDebt !== null &&
                            item.totalShortTermDebt !== undefined
                              ? formatNumber(
                                  item.totalShortTermDebt.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="bs-table-cell"
                            >
                              <div className="bs-cell-content">
                                <span className="bs-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Shares outstanding */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <span
                        className="bs-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Shares outstanding',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Shares outstanding
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalCommonSharesOutstanding !== null &&
                        item.totalCommonSharesOutstanding !== undefined
                          ? formatNumber(
                              item.totalCommonSharesOutstanding.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Tangible book value */}
                  <tr className="bs-table-row">
                    <td></td>
                    <td className="bs-table-label-cell">
                      <span
                        className="bs-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Tangible book value',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Tangible book value
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.BVPS !== null && item.BVPS !== undefined
                          ? formatNumber(item.BVPS.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="bs-table-cell">
                          <div className="bs-cell-content">
                            <span className="bs-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <CommonTooltip
        ref={tooltipRef}
        content={tooltip.content}
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
      />
    </Layout>
  );
};

export default BalanceSheetWidget;
